<template>
    <div class="report__item__wrap">
        <div>
            <div class="flex align-items-start">
                <div class="flex-2 mr--10">
                    <BaseCard class="mb--35" shadow>
                        <template #card-body>
                            <div class="report__item__header">
                                <div class="mb--15">
                                    <FormLabel title="Monthly Distribution" label-type="title" class="mb--0" />
                                    <p class="text--xs text-gray-500">
                                        Showing distribution of projects created on a monthly basis
                                    </p>
                                </div>
                                <!-- <bar-chart :data="transformedData.projectsByMonth" /> -->
                                <CustomLineChart :status-data="transformedData.projectsByMonth" />
                            </div>
                        </template>
                    </BaseCard>

                    <div class="mb--35">
                        <div class="mb--15">
                            <FormLabel title="Project Currency Distribution" label-type="title" class="mb--0" />
                            <p class="text--xs text-gray-500">
                                Showing distribution of projects by payment currency
                            </p>
                        </div>
                        <PieChartReport field="project" :stats-data="transformedData.projectByCurrency" />
                    </div>
    
                    <div>
                        <div class="mb--15">
                            <FormLabel title="Project Payment Distribution" label-type="title" class="mb--0" />
                            <p class="text--xs text-gray-500">
                                Showing distribution of projects by project payment type
                            </p>
                        </div>
                        <PieChartReport field="project" :stats-data="transformedData.projectType" />
                    </div>
                </div>
                <div class="flex-1">
                    <div class="mb--25">
                        <div class="mb--15">
                            <FormLabel title="Status count" label-type="title" class="mb--0" />
                            <p class="text--xs text-gray-500">
                                Showing project count grouped by status
                            </p>
                        </div>
                        <StatusDistribution  
                            :status-data="statusData"
                            label="Status Count"
                            field="project"
                        />
                    </div>
    
                    <div>
                        <div class="mb--15">
                            <FormLabel title="Status percentage" label-type="title" class="mb--0" />
                            <p class="text--xs text-gray-500">
                                Showing project percentage grouped by status
                            </p>
                        </div>
                        <StatusPercentage  
                            :status-data="ratesData"
                            label="Percentage Status"
                        />
                    </div>

                    <div class="mt--30">
                        <div class="mb--15">
                            <FormLabel title="Recent Projects" label-type="title" class="mb--0" />
                            <p class="text--xs text-gray-500">
                                Showing your most recently created projects
                            </p>
                        </div>

                        <div>
                            <template v-if="mostRecentState === 'loading'">
                                <PageSpinner />
                            </template>

                            <template v-else-if="mostRecentState === 'error'">
                                <EmptyPage state="error" @action="handleFetchMostRecent" />
                            </template>

                            <template v-else>
                                <div class="grid gap-2">
                                    <template v-if="recentProjects.length">
                                        <BaseCard v-for="item in recentProjects" :key="item._id" shadow>
                                            <template #card-body>
                                                <div class="outer__wrap">
                                                    <div class="flex align-items-center w-100 justify-content-between">
                                                        <div class="flex align-items-center w-100 mt--3">
                                                            <IconWrap icon-name="folder" width="12px" height="12px" style="width: 30px; height: 30px;" />
                                                            <div class="ml--10 flex-2">
                                                                <p class="text--capitalize text--sm text--medium text-gray-600">
                                                                    {{ item.title }}
                                                                </p>
                                                                <div>
                                                                    <Status type="project" :value="item.status" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div class="cursor-pointer" @click="goToDetailsPage(item._id)">
                                                            <IconSvg name="right-arrow-alt" fill="#666" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </BaseCard>
                                    </template>
                                    <template v-else>
                                        <EmptyPage 
                                            state="empty_custom" 
                                            icon-name="report" 
                                            type="data"
                                            title=" No data available." 
                                        />
                                    </template>
                                </div>
                            </template>
                        </div>
                    </div>
    
                    <!-- <Stats
                        title="Average Project Completed"
                        :value="transformedData.avgCompletedProjects"
                        class="mb--25 h-auto mt--20"
                        :show-percentage="false"
                        :format-val="0"
                    />
                    <Stats
                        title="Average Project Created"
                        :value="transformedData.avgProjects"
                        class="mt--20"
                        :show-percentage="false"
                        :format-val="0"
                    /> -->
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { BaseCard, EmptyPage, IconSvg, PageSpinner, IconWrap, Status, FormLabel } from "@/components/ui/index";
import { getMonthFromNum } from "@/utils/date";
import { projectFilterStatuses }from "@/utils/enums";
import StatusDistribution from "../stats/StatusDistribution.vue";
import StatusPercentage from "../stats/StatusPercentage.vue";
import PieChartReport from "../stats/PieChartReport.vue";
import CustomLineChart from "../stats/CustomLineChart.vue";

export default {
    name: "ProjectReports",

    components: {
        BaseCard,
        StatusDistribution,
        StatusPercentage,
        PieChartReport,
        FormLabel,
        CustomLineChart,
        IconSvg,
        BaseCard,
        EmptyPage,
        PageSpinner,
        Status,
        IconWrap,
    },

    data() {
        return {
            projectFilterStatuses,
            mostRecentState: false,
        };
    },

    computed: {
        reportData() {
            return this.$store.getters["report/reportData"];
        },

        reportDataProps() {
            return this.reportData?.projects;
        },

        transformedData() {
            if (!this.reportDataProps) return {};

            const data = {};
            const keys = Object.keys(this.reportDataProps);

            if (!keys && keys.length) return {};

            for (let i = 0; i < keys.length; i++) {
                const item = keys[i];
                const list = this.reportDataProps[item]; 

                switch(item) {
                case "projectsByMonth":
                    data.projectsByMonth = list.map(i => ([this.getMonthFromNum(i?._id), i?.count])) || [];
                    break;

                case "projectType":
                    data.projectType = list.map(x => ([x._id, x.count])) || [];
                    break;

                case "projectByCurrency":
                    data.projectByCurrency = list.map(x => ([x._id, x.count])) || [];
                    break;

                case "projectsByStatus":
                    data.projectsByStatus = list || [];
                    break;

                case "totalNum":
                    data.totalNum = list.length && list[0]?.count || 0;
                    break;

                case "averageCreatedProject":
                    data.avgProjects = list.length && list[0]?.avgProjects || 0;
                    break;
               
                case "averageCompletedProject":
                    data.avgCompletedProjects = list.length && list[0]?.avgCompletedProjects || 0;
                    break;

                default:
                    break;
                }
            }

            return data;
        },

        totalNum() {
            return this.transformedData?.totalNum;
        },

        ratesData() {
            const statusMap = new Map(this.transformedData.projectsByStatus?.map(item => item && [item._id, item.count]));
            const totalNum = this.transformedData.totalNum;
            
            return projectFilterStatuses.map(item => {
                const count = statusMap.get(item) || 0;
                const percentageValue = totalNum > 0 ? ((count / totalNum) * 100).toFixed(0) : "0";
                
                return {
                    id: item,
                    percentageValue,
                };
            });
        },

        statusData() {
            const statusMap = new Map(this.transformedData.projectsByStatus?.map(item => item && [item._id, item.count]));
            
            return projectFilterStatuses.map(item => {
                const count = statusMap.get(item) || 0;
                
                return {
                    id: item,
                    count,
                };
            });
        },

        recentProjects() {
            return this.$store.getters["report/recentProjects"];
        },
    },

    async mounted() {
        await this.handleFetchMostRecent();
    },

    methods: {
        getMonthFromNum,

        async handleFetchMostRecent() {
            try {                
                this.mostRecentState = "loading";
                await this.$store.dispatch("report/fetchMostRecentProjects", { refresh: true });
                this.mostRecentState = "";
            } catch (error) {
                this.mostRecentState = "error";
            }
        },

        goToDetailsPage(id) {
            if (!id) return;
            this.$router.push(`/dashboard/projects/${id}`);
        },
    },
};
</script>